import React, { useContext, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import OfficeFormType from '../enums/office-form-types';
import type Room from '../models/room/room';
import * as styles from './modal.styles';
import { IsMobileContext } from '../../../core/context/is-mobile';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import Row from '../../../core/components/row/row.component';
import AppConstants from '../../../core/app.constants';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly room: Room;
  readonly handleRoomChange: (formType: OfficeFormType, value: unknown) => void;
  readonly handleCreateRoom: (room: Room) => void;
  /**
   * The office books from a floorplan, so the room's capacity is the number of desks drawn in it:
   * it starts at zero and is not asked for.
   */
  readonly capacityFollowsFloorPlan?: boolean;
}

const RoomModal = ({
  showModal, handleClose, room, handleRoomChange, handleCreateRoom, capacityFollowsFloorPlan = false,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!showModal) {
      setIsLoading(false);
    }
  }, [showModal]);

  const handleSave = (roomToSave: Room): void => {
    setIsLoading(true);
    handleCreateRoom(roomToSave);
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      <Column className={style(styles.modalBody(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <span className={style(styles.columnHeader(isMobile, 8))}>{t('create-room')}</span>
        <LabeledInput
          labelText={t('room-name').toString()}
          value={room.name}
          onChange={(value): void => {
            handleRoomChange(OfficeFormType.name, value);
          }}
        />
        {capacityFollowsFloorPlan ? (
          <span className={style(styles.bodyText)}>{t('create-room-floorplan-capacity-hint')}</span>
        ) : (
          <LabeledInput
            tooltipText={t('max-capacity-info-message').toString()}
            tooltipId={AppConstants.TOOLTIP_GENERAL_ID}
            labelText={t('max-capacity').toString()}
            value={room.capacity.toString()}
            type="number"
            onChange={(value): void => {
              handleRoomChange(OfficeFormType.capacity, Number(value));
            }}
          />
        )}
        <Row itemSpacing={12}>
          <LabeledInput
            tooltipText={t('threshold-min-info-message').toString()}
            tooltipId={AppConstants.TOOLTIP_GENERAL_ID}
            labelText={t('threshold-min').toString()}
            type="number"
            min={0}
            max={100}
            value={room.thresholdMinPercentage?.toString() ?? ''}
            onChange={(value): void => {
              handleRoomChange(OfficeFormType.roomThresholdMinPercentage, value === '' ? undefined : Number(value));
            }}
          />
          <LabeledInput
            tooltipText={t('threshold-max-info-message').toString()}
            tooltipId={AppConstants.TOOLTIP_GENERAL_ID}
            labelText={t('threshold-max').toString()}
            type="number"
            min={0}
            max={100}
            value={room.thresholdMaxPercentage?.toString() ?? ''}
            onChange={(value): void => {
              handleRoomChange(OfficeFormType.roomThresholdMaxPercentage, value === '' ? undefined : Number(value));
            }}
          />
        </Row>
        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              onClick={(): void => {
                handleSave(room);
              }}
              loading={isLoading}
            >
              {t('create')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default RoomModal;
